import React, { useEffect, useState } from "react";
import {
  Button,
  Flex,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Input,
  Icon,
  Text,
  ColorPickerControl
} from "@theorgchartdev/storybook";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { ComplexRuleInput } from "@/appComponents/Blocks/Composites/RuleInput/ComplexRuleInput/ComplexRuleInput";
import { GetWSMgr } from "@/Utils/StateLib";
import { getLocalizedColorPickerStrings } from "@/Utils/Utils";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { QChartTemplateRule } from "@coreapp/CoreApp/QChartTemplateRule";
import {
  enumMatchSubs,
  kDefaultNewHighlightColor,
  kPickerColors,
  kReservedFields
} from "@coreapp/constants";
import { IMPORT_FILTERS_TEST_PREFIX } from "./ImportFiltersUtils";

const FiltersHighlightsEditModal = ({
  show,
  onClose,
  handleUpdate,
  showHighlight,
  showFilter,
  chartConfig,
  edit: editEntityIndex,
  fieldIdx
}) => {
  if (!show) return;
  const [editFilter, setEditFilter] = useState({
    filterName: "",
    filterDef: ""
  });
  const [editHighlight, setEditHighlight] = useState({
    name: "",
    rule: "",
    color: ""
  });
  const [errorMsg, setErrorMsg] = React.useState("");
  const customColors = Globals._wsMgr.GetOrgInfo().GetCustomColors() ?? [];

  useEffect(() => {
    InitializeFormula();
  }, []);

  const InitializeFormula = () => {
    let initFormula = {};
    let filters = chartConfig.filters ?? [];
    let savedHighlights = chartConfig.highlights ?? [];
    if (editEntityIndex !== null) {
      if (showHighlight) {
        initFormula = JSON.parse(JSON.stringify(savedHighlights[fieldIdx]));
        setEditHighlight(initFormula);
      } else {
        initFormula = JSON.parse(JSON.stringify(filters[fieldIdx]));
        setEditFilter(initFormula);
      }
    }

    // Add default highlight color
    if (editEntityIndex === null) {
      let defaultHighlight = kDefaultNewHighlightColor;
      const usedHighlights = savedHighlights.map(h => h.color);
      const availableHighlights = customColors
        .concat(kPickerColors)
        .filter(h => !usedHighlights.includes(h));

      if (availableHighlights.length > 0 && !availableHighlights.includes(defaultHighlight)) {
        defaultHighlight = availableHighlights[0];
      }

      setEditHighlight({ ...editHighlight, color: defaultHighlight });
    }
  };
  let curIndex = null;

  if (editEntityIndex != null) {
    if (showHighlight) {
      curIndex = fieldIdx;
    } else {
      curIndex = fieldIdx;
    }
  }

  const [editedRule, setEditRule] = useState(() => {
    const rule = new QChartTemplateRule();
    rule.InitChartTemplateRule(enumMatchSubs.matchonly, 0, false);
    rule.SetRuleCriteria("Complex Rule");
    if (editEntityIndex != null) {
      if (showHighlight) {
        const formulas = chartConfig.highlights ?? [];
        rule.SetComplexDef(formulas[editEntityIndex]?.rule ?? "");
      } else {
        const formulas = chartConfig.filters ?? [];
        rule.SetComplexDef(formulas[editEntityIndex]?.filterDef ?? "");
      }
    }
    return rule;
  });

  const CreateTitleName = () => {
    let title = "";
    if (showFilter) {
      if (curIndex != null) {
        title = appstr("Edit filter");
      } else title = appstr("Create filter");
    }
    if (showHighlight) {
      if (curIndex != null) {
        title = appstr("Edit highlight");
      } else title = appstr("Create highlight");
    }
    return title;
  };

  const GetFHName = () => {
    let name = "";
    if (showFilter) {
      name = editFilter.filterName;
    }
    if (showHighlight) {
      name = editHighlight.name;
    }
    return name;
  };

  const SetFHName = option => {
    ValidateFormula(editedRule, option);
    if (showFilter) {
      setEditFilter({ ...editFilter, filterName: option });
    }
    if (showHighlight) {
      setEditHighlight({ ...editHighlight, name: option });
    }
  };

  const SetHighlightColor = option => {
    setEditHighlight({ ...editHighlight, color: option });
  };

  const ValidateFormula = (ruleDef, name) => {
    let nextError = "";

    if (name.length < 1) {
      nextError = appstr("formulaerrnoname");
    }

    const reservedFields = kReservedFields.map(item => {
      return item.toLowerCase();
    });

    const indexReservedField = reservedFields.indexOf(name.toLowerCase());
    if (indexReservedField > -1) {
      const failureAlert = kReservedFields[indexReservedField] + appstr("formulareservedname");
      setErrorMsg(failureAlert);
      return false;
    }

    if (showHighlight) {
      const formulaList = chartConfig["highlights"] ?? [];
      for (let aFormulaIdx in formulaList) {
        const existingFormulaName = formulaList[aFormulaIdx]["name"];
        if (curIndex != null) {
          if (formulaList[curIndex] != formulaList[aFormulaIdx] && name == existingFormulaName) {
            nextError = appstr("formulaerrdupname");
            break;
          }
        } else if (name == existingFormulaName) {
          nextError = appstr("formulaerrdupname");
          break;
        }
      }
    } else {
      const formulaList = chartConfig["filters"] ?? [];
      for (let aFormulaIdx in formulaList) {
        const existingFormulaName = formulaList[aFormulaIdx]["filterName"];
        if (curIndex != null) {
          if (formulaList[curIndex] != formulaList[aFormulaIdx] && name == existingFormulaName) {
            nextError = appstr("formulaerrdupname");
            break;
          }
        } else if (name == existingFormulaName) {
          nextError = appstr("formulaerrdupname");
          break;
        }
      }
    }

    setErrorMsg(nextError);

    const validationResult = ruleDef.ValidateRule();
    const isValid = validationResult === "success";

    if (!isValid || nextError.length > 0) {
      return false;
    }

    return true;
  };

  const BuildFormulaDef = () => {
    let formulaDefinition = editedRule.GetComplexDef();
    if (showFilter) {
      return {
        ...editFilter,
        filterDef: formulaDefinition
      };
    }
    if (showHighlight) {
      return {
        ...editHighlight,
        rule: formulaDefinition
      };
    }
  };

  const SaveChanges = () => {
    const newFormula = BuildFormulaDef();
    const name = GetFHName();
    let isValid = ValidateFormula(editedRule, name);
    if (!isValid) return false;
    let existingFilters = chartConfig["filters"];
    let existingHighlights = chartConfig["highlights"];
    if (editEntityIndex != null && editEntityIndex > -1 && showFilter) {
      existingFilters[curIndex] = newFormula;
      handleUpdate(existingFilters, "filters");
    }
    if (editEntityIndex == null && showFilter) {
      existingFilters.push(newFormula);
      handleUpdate(existingFilters, "filters");
    }
    if (editEntityIndex != null && editEntityIndex > -1 && showHighlight) {
      existingHighlights[curIndex] = newFormula;
      handleUpdate(existingHighlights, "highlights");
    }
    if (editEntityIndex == null && showHighlight) {
      existingHighlights.push(newFormula);
      handleUpdate(existingHighlights, "highlights");
    }
    onClose();
  };

  const onCancel = (skipDirtyCheck = false) => {
    const wsMgr = GetWSMgr();
    if (showHighlight && !skipDirtyCheck) {
      wsMgr.CreateReactAlert(
        appstr("Save changes to") + editHighlight.name + "?",
        appstr("All changes will be lost"),
        () => SaveChanges(),
        () => onCancel(true),
        appstr("Save Changes"),
        appstr("Discard Changes")
      );
    }
    if (showFilter && !skipDirtyCheck) {
      wsMgr.CreateReactAlert(
        appstr("Save changes to filter") + editFilter.filterName + "?",
        appstr("All changes will be lost"),
        () => SaveChanges(),
        () => onCancel(true),
        appstr("Save Changes"),
        appstr("Discard Changes")
      );
      return;
    }
    onClose(false);
  };

  const dataTestIdPrefix = showHighlight
    ? `${IMPORT_FILTERS_TEST_PREFIX}-edit-highlights-modal`
    : `${IMPORT_FILTERS_TEST_PREFIX}-edit-filters-modal`;

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="edit-fh-modal"
        show={show}
        centerScreen={true}
        minWidth={"large"}
        maxWidth={"large"}
        customStyle={{ minHeight: "400px", maxHeight: "500px" }}
        className={"oc-modal-content"}
      >
        <DialogTitle
          divider={true}
          onClose={() => onClose()}
          data-testid={`${dataTestIdPrefix}-title`}
        >
          <span className={"ocn-explorer-icon-label"}>
            <Icon size={"medium"} name={"pen"} />
            {CreateTitleName()}
          </span>
        </DialogTitle>
        <DialogContent customContentClass={"oc-modal-content-container"}>
          <Flex className={"oc-modal-content"} direction={"column"} gap={"8px"} height={"100%"}>
            <Input
              type="text"
              size="medium"
              onChange={e => SetFHName(e.target.value)}
              label={appstr("Name")}
              placeholder={appstr("Name")}
              value={GetFHName()}
              message={errorMsg}
              status={errorMsg.length > 0 ? "error" : undefined}
              data-testid={"edit-fh-name-input"}
            />
            <Flex direction="column">
              <ComplexRuleInput
                editing
                rule={editedRule}
                setRule={setEditRule}
                dataTestIdPrefix={"edit-fh-rule"}
              />
            </Flex>
            {showHighlight && (
              <Flex direction={"column"} gap={"8px"} width={"100%"} height={"100%"}>
                <Flex direction={"column"} gap={"8px"}>
                  <Text size={"small"} modifier={"bold"}>
                    {appstr("highlightcolor")}
                  </Text>
                </Flex>
                <Flex direction={"column"} gap={"8px"}>
                  <ColorPickerControl
                    currentColor={editHighlight.color}
                    showNoFill
                    swatchShape={"circle"}
                    swatchSize={"small"}
                    onChange={SetHighlightColor}
                    customColors={customColors}
                    textTags={getLocalizedColorPickerStrings()}
                    data-testid={`${dataTestIdPrefix}-color-picker`}
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
        </DialogContent>
        <DialogActions divider={true}>
          <Flex className={"oc-right-justify"} padding={"4px"}>
            <DialogActions.Tertiary
              size={"medium"}
              onClick={() => onCancel()}
              data-testid={`${dataTestIdPrefix}-cancel`}
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary
              size={"medium"}
              onClick={SaveChanges}
              dataTestId={`${dataTestIdPrefix}-save`}
            >
              {appstr("Save")}
            </DialogActions.Primary>
          </Flex>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};

export default FiltersHighlightsEditModal;
